import {
  Component,
  DestroyRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject, forkJoin, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import {
  YdsButtonComponent,
  YdsCardComponent,
  YdsEmptyStatesComponent,
  YdsSpinnerComponent,
  YdsTableComponent,
  YdsSplitButtonComponent,
  YdsTableCellDirective,
  YdsPaginationComponent,
  type YdsTableColumn,
  type YdsSplitButtonItem,
} from '../../../yds-angular';
import { formatDisplayDate } from '../../../shared/general-ledger-yds.utils';
import { ExportDefinition } from '../../../model/export-definition.model';
import { ExportRule, SelectionCodeOption } from '../../../model/export-rule.model';
import { ExportDefinitionService } from '../export-definition.service';
import { ExportRuleService } from '../export-rule.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { deleteExportDefinitionWithChecks, exportLineSummary } from '../exportdefinition-shared';
import { ExportruleDialogComponent } from '../exportrule-dialog/exportrule-dialog.component';
import { ExportdefinitioncopyDialogComponent } from '../exportdefinitioncopy-dialog/exportdefinitioncopy-dialog.component';

const MAX_END_DATE = '31-12-9999';

interface ExportDefinitionRow {
  definition: ExportDefinition;
  rules: ExportRule[];
}

@Component({
  selector: 'app-exportdefinition-list',
  imports: [
    YdsCardComponent,
    YdsButtonComponent,
    YdsTableComponent,
    YdsSpinnerComponent,
    YdsEmptyStatesComponent,
    YdsSplitButtonComponent,
    YdsTableCellDirective,
    YdsPaginationComponent,
    ExportruleDialogComponent,
    ExportdefinitioncopyDialogComponent,
  ],
  templateUrl: './exportdefinition-list.component.html',
  styleUrl: './exportdefinition-list.component.scss',
})
export class ExportdefinitionListComponent implements OnInit {
  readonly pageSize = 10;
  readonly tableColumns: YdsTableColumn[] = [
    { key: 'exportDefinitionName', header: 'Exportdefinitie' },
    { key: 'bedrijf', header: 'Bedrijf' },
    { key: 'startDate', header: 'Begindatum' },
    { key: 'endDate', header: 'Einddatum' },
    { key: 'exportRules', header: 'Exportregels' },
    { key: 'actions', header: 'Acties', width: '180px' },
  ];

  readonly splitButtonItems: readonly YdsSplitButtonItem[] = [
    { id: 'edit', label: 'Wijzigen' },
    { id: 'exportrule', label: 'Exportregel toevoegen' },
    { id: 'delete', label: 'Verwijderen' },
  ];

  private router = inject(Router);
  private exportDefinitionService = inject(ExportDefinitionService);
  private exportRuleService = inject(ExportRuleService);
  private dialogService = inject(ConfirmDialogService);
  private destroyRef = inject(DestroyRef);
  private document = inject(DOCUMENT);
  private injector = inject(Injector);

  readonly rows: WritableSignal<ExportDefinitionRow[]> = signal([]);
  readonly kindOptions = signal<SelectionCodeOption[]>([]);
  readonly headerLevelOptions = signal<SelectionCodeOption[]>([]);
  readonly pageIndex = signal(0);
  readonly isLoading = signal(true);

  // Add-rule popup state.
  readonly dialogOpen = signal(false);
  readonly dialogDefinitionId = signal('');
  readonly dialogExistingKinds = signal<string[]>([]);

  // Copy-definition popup state.
  readonly copyDialogOpen = signal(false);

  /** Definition to scroll to after the next reload; the list keeps its API order. */
  private readonly focusId = signal('');

  private reload$ = new Subject<void>();

  readonly pagedRows = computed(() => {
    const start = this.pageIndex() * this.pageSize;
    return this.rows().slice(start, start + this.pageSize);
  });

  readonly tableItems = computed(() =>
    this.pagedRows().map((row) => ({
      id: row.definition.exportDefinitionId,
      exportDefinitionName: row.definition.exportDefinitionName,
      bedrijf: 'N.v.t.',
      startDate: formatDisplayDate(row.definition.startDate, 'nl-NL'),
      endDate: this.formatEndDate(row.definition.endDate),
      exportRules:
        exportLineSummary(
          this.kindOptions(),
          row.rules.map((rule) => rule.kindOfExportLine),
        ) || 'Geen',
    })),
  );

  constructor() {
    this.reload$
      .pipe(
        switchMap(() => {
          this.isLoading.set(true);
          this.rows.set([]);
          return this.exportDefinitionService.getByContext().pipe(
            switchMap((definitions) => {
              if (definitions.length === 0) {
                return of([] as ExportDefinitionRow[]);
              }
              return forkJoin(
                definitions.map((definition) =>
                  this.exportRuleService.getByExportDefinition(definition.exportDefinitionId).pipe(
                    catchError(() => of([] as ExportRule[])),
                    switchMap((rules) => of({ definition, rules })),
                  ),
                ),
              );
            }),
            catchError(() => {
              this.isLoading.set(false);
              this.rows.set([]);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (rows) => {
          const focusId = this.focusId();
          this.rows.set(rows);
          this.pageIndex.set(this.pageOf(focusId, rows));
          this.focusId.set('');
          this.isLoading.set(false);
          if (focusId) {
            this.scrollRowIntoView(focusId);
          }
        },
      });
  }

  /** Page holding the given definition, or the first page when it is not found. */
  private pageOf(id: string, rows: ExportDefinitionRow[]): number {
    const index = id
      ? rows.findIndex((row) => row.definition.exportDefinitionId === id)
      : -1;
    return index < 0 ? 0 : Math.floor(index / this.pageSize);
  }

  /**
   * Scrolls the row into view. Uses 'scrollIntoView' so every scrollable ancestor
   * is taken into account, and retries for a few frames because the table is only
   * rendered once the loading state has been cleared.
   */
  private scrollRowIntoView(id: string): void {
    const selector = `tr[data-row-key="${CSS.escape(id)}"]`;
    let attempts = 30;

    const tryScroll = () => {
      const row = this.document.querySelector(selector) as HTMLElement | null;
      if (row) {
        row.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      if (attempts-- > 0) {
        requestAnimationFrame(tryScroll);
      }
    };

    afterNextRender(tryScroll, { injector: this.injector });
  }

  ngOnInit(): void {
    this.loadSelectionLists();
    this.loadDefinitions();
  }

  private loadSelectionLists(): void {
    this.exportRuleService
      .getKindOfExportLineOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => this.kindOptions.set(options));

    this.exportRuleService
      .getHeaderLevelOptions()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((options) => this.headerLevelOptions.set(options));
  }

  loadDefinitions(): void {
    this.focusId.set('');
    this.reload$.next();
  }

  navigateToCreate(): void {
    this.router.navigate(['/config/exportdefinition/editable']);
  }

  onTableRowClick(row: Record<string, unknown>): void {
    this.viewItem(String(row['id']));
  }

  viewItem(id: string, event?: MouseEvent): void {
    event?.stopPropagation();
    this.router.navigate(['/config/exportdefinition/editable', id]);
  }

  handleActionClick(id: string, actionId: string): void {
    if (actionId === 'edit') {
      this.viewItem(id);
      return;
    }
    if (actionId === 'exportrule') {
      this.openRuleDialog(id);
      return;
    }
    if (actionId === 'delete') {
      this.deleteItem(id);
    }
  }

  private openRuleDialog(id: string): void {
    const row = this.rows().find((r) => r.definition.exportDefinitionId === id);
    this.dialogDefinitionId.set(id);
    this.dialogExistingKinds.set((row?.rules ?? []).map((rule) => rule.kindOfExportLine));
    this.dialogOpen.set(true);
  }

  onRuleSaved(): void {
    this.loadDefinitions();
  }

  openCopyDialog(): void {
    this.copyDialogOpen.set(true);
  }

  onCopied(newId: string): void {
    this.focusId.set(newId);
    this.reload$.next();
  }

  private deleteItem(id: string): void {
    const name =
      this.rows().find((r) => r.definition.exportDefinitionId === id)?.definition
        .exportDefinitionName ?? '';

    deleteExportDefinitionWithChecks({
      id,
      exportDefinitionName: name,
      service: this.exportDefinitionService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.loadDefinitions(),
    });
  }

  private formatEndDate(endDate: ExportDefinition['endDate']): string {
    const formatted = formatDisplayDate(endDate, 'nl-NL');
    return formatted === MAX_END_DATE ? 'N.v.t.' : formatted;
  }
}
